import BaseEntity 		 	 from "@/core/misc/BaseEntity";
import Core 				 from "@/core/Core";
import Utils 				 from "@/core/utils/Utils";
import Title_ru_RU           from "@/core/misc/decorators/Title_ru_RU";
import serializeSimplified   from "@/core/misc/decorators/SerializeSimplified";
import Country 				 from "@/domains/Countries/Country";
import Company 				 from "@/domains/Companies/Company";
import TimeZone 			 from "@/domains/TimeZones/TimeZone";
import PartnerCategory 		 from "@/domains/PartnerCategories/PartnerCategory";
import PartnersService       from "@/domains/Partners/PartnersService";
import Person 				 from "@/domains/Persons/Person";
import Store 				 from '@/domains/Stores/Store'

import TYPES from "@/core/services/Changelogs/types";

import {
  isSellerResolver,
  isBuyerResolver,
  isDelivererResolver,
  isOperationsAllowed, countryResolver
} from './PartnersFeedResolvers'
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";

@Title_ru_RU_Class('Партнёр')
class Partner extends BaseEntity {
  /** наименование */
  @Title_ru_RU('Название', TYPES.STRING)
  public name: string | null;

  /** продавец */
  @Title_ru_RU('Продавец', TYPES.BOOLEAN, isSellerResolver)
  public isSeller: boolean;
  /** покупатель */
  @Title_ru_RU('Покупатель', TYPES.BOOLEAN, isBuyerResolver)
  public isBuyer: boolean;
  /** транспортная компания */
  @Title_ru_RU('Транспортная компания', TYPES.BOOLEAN, isDelivererResolver)
  public isDeliverer: boolean;

  /** Разрешено ли поставщику размещать заказы/предложения */
  @Title_ru_RU('Тип доступа', TYPES.BOOLEAN, isOperationsAllowed)
  public isOperationsAllowed: boolean;

  /** страна */
  @Title_ru_RU('Страна', TYPES.OBJECT, countryResolver)
  public country: Country;
  /** категория */
  @serializeSimplified
  @Title_ru_RU('Категория', TYPES.OBJECT)
  public category: PartnerCategory | null;
  /** Ответственные */
  @serializeSimplified
  @Title_ru_RU('Ответственный', TYPES.STRING)
  public responsible: Person[];
  /** часовой пояс */
  @serializeSimplified
  @Title_ru_RU('Часовой пояс', TYPES.OBJECT)
  public mainTimeZone: TimeZone | null;
  /** Ссылка на сайт */
  @Title_ru_RU('Ссылка на сайт', TYPES.STRING)
  public webSiteUrl: string | null;

  /** TODO: докинуть на беке имя и выводить в реестре, если нужна будет колонка и фильтр */

  /** представители */
  @Title_ru_RU('Сотрудники', TYPES.OBJECT)
  public persons: Person[];
  /** юр. лица */
  @Title_ru_RU('Компании', TYPES.OBJECT)
  public companies: Company[];
  /** склады (либо хранилище) */
  @Title_ru_RU('Склады', TYPES.OBJECT)
  public stores: Store[];

  /** TODO: докинуть contracts чтобы выводить фильтр "есть договор" */

  /** Поле для хранения количества юр. лиц/контактов и т.д. */
  public stats: object;

  @Title_ru_RU('Кредитный лимит', TYPES.NUMBER)
  public creditLimitAmount: number = 0;
  @Title_ru_RU('Отсрочка платежа', TYPES.NUMBER)
  public paymentDeferralDays: number = 0;
  @Title_ru_RU('Комментарий к кредитному лимиту', TYPES.STRING)
  public creditLimitComment: string | null;

  static fromNew(partner = new Partner()): Partner {
    partner.responsible = [];
    partner.persons     = [];
    partner.companies   = [];
    partner.stores      = [];

    return partner;
  }

  /**
   * Партнёр из свободного ввода VSuggestionSelectV2.
   *
   * Вводом партнёра заводят там, где его выбирают поставщиком (вариант
   * заявки, предложение), поэтому isSeller ставим сразу — партнёр без роли
   * бесполезен, а другой роли в этом сценарии не бывает.
   *
   * Страна: допущение, что заведённый вводом партнёр всегда местный, то есть
   * его страна совпадает со страной текущего партнёра-покупателя. Раньше это
   * допущение жило комментарием в форме, которая дёргала CountriesService
   * по коду страны покупателя ради одного названия.
   *
   * Идём через fromNew, а не через базовую реализацию: та заполняет только
   * name, а коллекции остаются undefined, и потребители, которые их
   * перебирают (companies, stores), падают ещё до сохранения.
   */
  static fromSuggestion(name: string): Partner {
    const partner = Partner.fromNew();

    partner.name     = name;
    partner.isSeller = true;

    if (Core.currentPartner.isBuyer === true) {
      partner.country = Core.currentPartner.country;
    }
    else {
      // Текущий партнёр не покупатель — допущение «поставщик местный» не
      // работает. Страну оставляем пустой: пусть заполнит бэк или человек,
      // но не мы наугад.
      Core.warn('Partner.fromSuggestion: текущий партнёр не покупатель, страна нового поставщика не заполнена');
    }

    return partner;
  }

  get type() {
    if (this.isSeller) return 'seller';
    if (this.isBuyer) return 'buyer';
    if (this.isDeliverer) return 'deliverer';
    return null;
  }

  set type(id: string | null) {
    this.isSeller = id === 'seller';
    this.isBuyer = id === 'buyer';
    this.isDeliverer = id === 'deliverer';
  }

  static fromServer(data: any, partner = new Partner()): Partner {
    super.fromServer(data, partner);

    if (Utils.isObject(data.country)) {
      partner.country = Country.fromServer(data.country);
    }

    partner.category = Utils.hydrate(data.category, PartnerCategory.fromServer);
    partner.responsible = Utils.hydrateArray(data.responsible, Person.fromServer);
    partner.mainTimeZone = Utils.hydrate(data.mainTimeZone, TimeZone.fromServer);

    partner.persons = Utils.hydrateArray(data.persons, Person.fromServer);
    partner.companies = Utils.hydrateArray(data.companies, Company.fromServer);
    partner.stores = Utils.hydrateArray(data.stores, Store.fromServer);

    return partner;
  }

  get typeName() {
    const partnerType = PartnersService.partnerTypeList.find(item => item.id === this.type);
    return partnerType ? partnerType.name : "Партнёр";
  }

  get isNamangan() {
    return this.id === 497
  }

  // Лаборатория современного здоровья
  // Modern Health Laboratory
  // ЛСЗ
  get isLSZ() {
    return this.id === 67;
  }

  /** Основной покупатель — ЛСЗ или Наманган. */
  get isPrimaryBuyer(): boolean {
    return this.isLSZ === true || this.isNamangan === true;
  }

  /** Все прочие покупатели. */
  get isSecondaryBuyer(): boolean {
    return this.isPrimaryBuyer === false && this.isBuyer === true;
  }

  getStoresByTransportZone(transportZoneId: number): Store[] {
    if (!this.stores?.length) {
      return [];
    }

    return this.stores.filter(store => store.transportZone?.id === transportZoneId);
  }

}

export default Partner;
